import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Flex, ButtonV2, BoxProps, IconV2Props } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { withWindow } from '@shared/utils/withWindow';

type MobileTransparentDrawerProps = Readonly<{
  children: React.ReactNode;
  isOpenByDefault?: boolean;
  scrollTopCta: {
    icon: React.FC<IconV2Props>;
    text: string;
    closeOnClick: boolean;
  };
  minHeight?: BoxProps['minHeight'];
  scrollToTop?: boolean;
}>;

const SCROLL_TO_MIDDLE_OFFSET = 1.7; // Divisor: scrolls to ~59% of screen height (innerHeight / 1.7)
const SCROLL_TO_TOP_OFFSET = 1.12; // Divisor: scrolls to ~12% of screen height (innerHeight * 0.12)

export const MobileTransparentDrawer = ({ children, scrollTopCta, minHeight = '100vh', isOpenByDefault = false, scrollToTop = false }: MobileTransparentDrawerProps) => {
  const [isOpen, setIsOpen] = useState(isOpenByDefault);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [translateY, setTranslateY] = useState(0);
  const transparentAreaRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const touchStartRef = useRef<number>(0);

  const ScrollTopCtaIcon = scrollTopCta.icon;

  const handleClose = useEventCallback(() => {
    setIsOpen(false);
  });

  const handleTouchStart = useEventCallback((e: React.TouchEvent) => {
    touchStartRef.current = e.touches[0].clientY;
    setTranslateY(0);
  });

  const handleTouchMove = useEventCallback((e: React.TouchEvent) => {
    const touchDelta = touchStartRef.current - e.touches[0].clientY;
    withWindow(global => {
      const maxHeight = global.innerHeight * 0.75; // Only move up 75% of screen height
      const newTranslateY = Math.min(Math.max(touchDelta, 0), maxHeight);
      setTranslateY(newTranslateY);

      if (touchDelta > global.innerHeight * 0.1) {
        // Open container at 20% screen height
        setIsOpen(true);
        setTranslateY(0); // Reset position when opening
      }
    });
  });

  const handleTouchEnd = useEventCallback(() => {
    if (!isOpen) {
      setTranslateY(0); // Reset position if not opened
    }
  });

  const handleTriggerClick = useEventCallback(() => {
    setIsOpen(true);
  });

  const scrollToPosition = useCallback(() => {
    if (containerRef.current) {
      withWindow(global => {
        containerRef.current?.scrollTo({ top: global.innerHeight / (scrollToTop ? SCROLL_TO_TOP_OFFSET : SCROLL_TO_MIDDLE_OFFSET), behavior: 'smooth' });
      });
    }
  }, [containerRef, scrollToTop]);

  const handleScroll = useCallback(() => {
    if (containerRef.current) {
      const { scrollTop } = containerRef.current;
      if (scrollTop === 0) {
        setIsOpen(false);
      }
      withWindow(global => {
        setShowScrollTop(scrollTop > global.innerHeight);
      });
    }
  }, [containerRef, setIsOpen, setShowScrollTop]);

  useEffect(() => {
    if (isOpen) {
      scrollToPosition();
    }
  }, [isOpen, scrollToPosition]);

  return (
    <>
      <Flex
        position="absolute"
        bottom={'-70vh'}
        left={0}
        right={0}
        backgroundColor="white"
        justifyContent={'center'}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        borderTopLeftRadius={4}
        borderTopRightRadius={4}
        boxShadow="0 -4px 12px rgba(0, 0, 0, 0.1)"
        padding={3}
        onClick={handleTriggerClick}
        cursor="pointer"
        zIndex={10}
        paddingY={5}
        height={'75vh'}
        style={{
          transform: `translateY(${-translateY}px)`,
          transition: 'none',
          willChange: 'transform',
        }}
      >
        <Box
          width={pxToRem(40)}
          height={'fit-content'}
          border={'1px solid'}
          borderColor={'mono8'}
          borderRadius={1}
        />
      </Flex>
      <Box
        ref={containerRef}
        onScroll={handleScroll}
        display={isOpen ? 'block' : 'none'}
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        top={0}
        background="transparent"
        zIndex={10}
        overflow="auto"
      >
        <Box
          ref={transparentAreaRef}
          height={'100vh'}
          backgroundColor="transparent"
          onClick={handleClose}
          cursor="pointer"
        />
        <Flex
          backgroundColor="white"
          minHeight={minHeight}
          flexDirection={'column'}
          alignItems={'center'}
          borderTopLeftRadius={4}
          borderTopRightRadius={4}
          boxShadow="0 -4px 12px rgba(0, 0, 0, 0.1)"
          position="relative"
        >
          {children}
          {showScrollTop && (
            <Box
              position="sticky"
              bottom={4}
              left={0}
              right={0}
              display="flex"
              justifyContent="center"
              zIndex={11}
              marginTop="auto"
            >
              <ButtonV2
                intent="neutral"
                minWidth={pxToRem(164)}
                paddingY={4}
                typographyVariant={'button2'}
                shape="rounded"
                startIcon={<ScrollTopCtaIcon />}
                onClick={scrollTopCta.closeOnClick ? handleClose : scrollToPosition}
                size="md"
              >
                {scrollTopCta.text}
              </ButtonV2>
            </Box>
          )}
        </Flex>
      </Box>
    </>
  );
};
